import { lazy } from "react"
import { Navigate, type RouteObject } from "react-router-dom"
import { RequireAuth } from "@/features/auth/RequireAuth"
import { RequireAdminAccess } from "@/features/admin/RequireAdminAccess"
import MainLayout from "@/widgets/layout/MainLayout"
import {
  PORTAL_DEPOSIT,
  PORTAL_EVENTS,
  PORTAL_EVENTS_CREATED,
  PORTAL_EVENTS_MODERATION,
  PORTAL_EVENTS_REGISTRATIONS,
  PORTAL_PROJECTS,
} from "@/app/router/portalRoutes"
import { RequireDepositModerationAccess } from "@/features/deposits/RequireDepositModerationAccess"
import { RequireProjectsPolicyAccess } from "@/features/projects/RequireProjectsPolicyAccess"
import { LegacyEventDetailRedirect } from "@/app/router/LegacyEventDetailRedirect"

const HomePage = lazy(() => import("@/pages/home/page"))
const EventsPage = lazy(() => import("@/pages/events/page"))
const EventDetailPage = lazy(() => import("@/pages/events/EventDetailPage"))
const DepositsPage = lazy(() => import("@/pages/deposits/page"))
const DepositDetailPage = lazy(() => import("@/pages/deposits/DepositDetailPage"))
const EventsPortalPage = lazy(() => import("@/pages/events/portal/page"))
const EventsPortalLayout = lazy(() => import("@/pages/events/portal/EventsPortalLayout"))
const EventPreviewPage = lazy(() => import("@/pages/events/portal/EventPreviewPage"))
const EventParticipantProfilePage = lazy(() => import("@/pages/events/portal/EventParticipantProfilePage"))
const EventAttendancePage = lazy(() => import("@/pages/events/portal/EventAttendancePage"))
const ProjectDetailPage = lazy(() => import("@/pages/projects/ProjectDetailPage"))
const ProjectsPortalLayout = lazy(() => import("@/pages/projects/portal/ProjectsPortalLayout"))
const ProjectsPortalOverviewPage = lazy(() => import("@/pages/projects/portal/ProjectsPortalOverviewPage"))
const ProjectsPortalRegistryPage = lazy(() => import("@/pages/projects/portal/ProjectsPortalRegistryPage"))
const ProjectsMyPage = lazy(() => import("@/pages/projects/portal/ProjectsMyPage"))
const ProjectsIntroPage = lazy(() => import("@/pages/projects/portal/ProjectsIntroPage"))
const ProjectsModerationPage = lazy(() => import("@/pages/projects/portal/ProjectsModerationPage"))
const ProjectsEvaluationPage = lazy(() => import("@/pages/projects/portal/ProjectsEvaluationPage"))
const ProjectsEvaluationProjectPage = lazy(
  () => import("@/pages/projects/portal/ProjectsEvaluationProjectPage"),
)
const ProjectsCriteriaPage = lazy(() => import("@/pages/projects/portal/ProjectsCriteriaPage"))
const ProjectsScoreboardPage = lazy(() => import("@/pages/projects/portal/ProjectsScoreboardPage"))
const ProjectsEvaluationSummaryByExpertsPage = lazy(
  () => import("@/pages/projects/portal/ProjectsEvaluationSummaryByExpertsPage"),
)
const ProjectsEvaluationSummaryByProjectsPage = lazy(
  () => import("@/pages/projects/portal/ProjectsEvaluationSummaryByProjectsPage"),
)
const ProjectsTeamPage = lazy(() => import("@/pages/projects/portal/ProjectsTeamPage"))
const ProjectsTeamInvitationsPage = lazy(() => import("@/pages/projects/portal/ProjectsTeamInvitationsPage"))
const DepositPortalLayout = lazy(() => import("@/pages/deposit/portal/DepositPortalLayout"))
const DepositsMyPage = lazy(() => import("@/pages/deposit/portal/DepositsMyPage"))
const DepositMyDetailPage = lazy(() => import("@/pages/deposit/portal/DepositMyDetailPage"))
const DepositIntroPage = lazy(() => import("@/pages/deposit/portal/DepositIntroPage"))
const DepositsModerationPage = lazy(() => import("@/pages/deposit/portal/DepositsModerationPage"))
const DepositModerationDetailPage = lazy(() => import("@/pages/deposit/portal/DepositModerationDetailPage"))
const DepositPortalOverviewPage = lazy(() => import("@/pages/deposit/portal/DepositPortalOverviewPage"))
const DepositPortalRegistryPage = lazy(() => import("@/pages/deposit/portal/DepositPortalRegistryPage"))
const ContactsPage = lazy(() => import("@/pages/contacts/page"))
const AboutPage = lazy(() => import("@/pages/about/page"))
const ProfilePage = lazy(() => import("@/pages/profile/page"))
const AdminPage = lazy(() => import("@/pages/admin/page"))
const AuthPage = lazy(() => import("@/pages/auth/page"))
const ResetPasswordPage = lazy(() => import("@/pages/reset-password/page"))
const ConfirmEmailPage = lazy(() => import("@/pages/confirm-email/page"))
const AgreementPage = lazy(() => import("@/pages/legal/AgreementPage"))
const PersonalDataConsentPage = lazy(() => import("@/pages/legal/PersonalDataConsentPage"))
const PolicyPrivacyPage = lazy(() => import("@/pages/legal/PolicyPrivacyPage"))
const ProjectConsentStubPage = lazy(() => import("@/pages/legal/ProjectConsentStubPage"))
const NotFound = lazy(() => import("@/pages/NotFound"))

const routes: RouteObject[] = [
  { path: "/login", element: <AuthPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  { path: "/confirm-email", element: <ConfirmEmailPage /> },
  {
    path: "/",
    element: <MainLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: "events", element: <EventsPage /> },
      { path: "events/portal", element: <Navigate to={PORTAL_EVENTS} replace /> },
      { path: "events/:id", element: <LegacyEventDetailRedirect /> },
      { path: "deposits", element: <DepositsPage /> },
      { path: "deposits/:id", element: <DepositDetailPage /> },
      {
        path: "portal",
        children: [
          { index: true, element: <Navigate to="events" replace /> },
          {
            path: "deposit",
            element: (
              <RequireAuth>
                <DepositPortalLayout />
              </RequireAuth>
            ),
            children: [
              { index: true, element: <DepositPortalOverviewPage /> },
              { path: "registry", element: <DepositPortalRegistryPage /> },
              { path: "registry/:id", element: <DepositDetailPage embeddedInPortal /> },
              { path: "my", element: <DepositsMyPage /> },
              { path: "my/:depositId", element: <DepositMyDetailPage /> },
              { path: "apply", element: <DepositIntroPage /> },
              { path: "apply/:depositId", element: <Navigate to="../my" replace /> },
              {
                path: "moderation",
                element: (
                  <RequireDepositModerationAccess>
                    <DepositsModerationPage />
                  </RequireDepositModerationAccess>
                ),
              },
              {
                path: "moderation/:depositId",
                element: (
                  <RequireDepositModerationAccess>
                    <DepositModerationDetailPage />
                  </RequireDepositModerationAccess>
                ),
              },
            ],
          },
          {
            path: "events",
            element: <EventsPortalLayout />,
            children: [
              {
                index: true,
                element: (
                  <RequireAuth>
                    <EventsPortalPage defaultTab="overview" />
                  </RequireAuth>
                ),
              },
              {
                path: "all",
                element: (
                  <RequireAuth>
                    <EventsPortalPage defaultTab="all" />
                  </RequireAuth>
                ),
              },
              {
                path: "created",
                element: (
                  <RequireAuth>
                    <EventsPortalPage defaultTab="created" />
                  </RequireAuth>
                ),
              },
              {
                path: "moderation",
                element: (
                  <RequireAuth>
                    <EventsPortalPage defaultTab="moderation" />
                  </RequireAuth>
                ),
              },
              {
                path: "registrations",
                element: (
                  <RequireAuth>
                    <EventsPortalPage defaultTab="registrations" />
                  </RequireAuth>
                ),
              },
              {
                path: "attendance",
                element: (
                  <RequireAuth>
                    <EventAttendancePage />
                  </RequireAuth>
                ),
              },
              {
                path: "preview/:eventId/participant/:userId",
                element: (
                  <RequireAuth>
                    <EventParticipantProfilePage />
                  </RequireAuth>
                ),
              },
              {
                path: "preview/:id",
                element: (
                  <RequireAuth>
                    <EventPreviewPage />
                  </RequireAuth>
                ),
              },
              { path: ":id", element: <EventDetailPage /> },
            ],
          },
          {
            path: "projects",
            element: (
              <RequireAuth>
                <ProjectsPortalLayout />
              </RequireAuth>
            ),
            children: [
              { index: true, element: <ProjectsPortalOverviewPage /> },
              {
                path: "registry",
                element: (
                  <RequireProjectsPolicyAccess area="registry">
                    <ProjectsPortalRegistryPage />
                  </RequireProjectsPolicyAccess>
                ),
              },
              { path: "my", element: <ProjectsMyPage /> },
              {
                path: "apply",
                element: (
                  <RequireProjectsPolicyAccess area="create">
                    <ProjectsIntroPage />
                  </RequireProjectsPolicyAccess>
                ),
              },
              {
                path: "moderation",
                element: (
                  <RequireProjectsPolicyAccess area="moderation">
                    <ProjectsModerationPage />
                  </RequireProjectsPolicyAccess>
                ),
              },
              {
                path: "evaluation",
                element: (
                  <RequireProjectsPolicyAccess area="evaluation">
                    <ProjectsEvaluationPage />
                  </RequireProjectsPolicyAccess>
                ),
              },
              {
                path: "evaluation/:projectId",
                element: (
                  <RequireProjectsPolicyAccess area="evaluation">
                    <ProjectsEvaluationProjectPage />
                  </RequireProjectsPolicyAccess>
                ),
              },
              {
                path: "criteria",
                element: (
                  <RequireProjectsPolicyAccess area="criteria">
                    <ProjectsCriteriaPage />
                  </RequireProjectsPolicyAccess>
                ),
              },
              {
                path: "scoreboard",
                element: (
                  <RequireProjectsPolicyAccess area="scoreboard">
                    <ProjectsScoreboardPage />
                  </RequireProjectsPolicyAccess>
                ),
              },
              {
                path: "evaluation-summary/experts",
                element: (
                  <RequireProjectsPolicyAccess area="moderation">
                    <ProjectsEvaluationSummaryByExpertsPage />
                  </RequireProjectsPolicyAccess>
                ),
              },
              {
                path: "evaluation-summary/projects",
                element: (
                  <RequireProjectsPolicyAccess area="moderation">
                    <ProjectsEvaluationSummaryByProjectsPage />
                  </RequireProjectsPolicyAccess>
                ),
              },
              {
                path: "team-invitations",
                element: (
                  <RequireProjectsPolicyAccess area="teamInvitations">
                    <ProjectsTeamInvitationsPage />
                  </RequireProjectsPolicyAccess>
                ),
              },
              {
                path: "team",
                element: (
                  <RequireProjectsPolicyAccess area="team">
                    <ProjectsTeamPage />
                  </RequireProjectsPolicyAccess>
                ),
              },
              { path: ":id", element: <ProjectDetailPage /> },
            ],
          },
          {
            path: "admin",
            element: (
              <RequireAuth>
                <RequireAdminAccess>
                  <AdminPage />
                </RequireAdminAccess>
              </RequireAuth>
            ),
          },
        ],
      },
      { path: "mycreatedevents", element: <Navigate to={PORTAL_EVENTS_CREATED} replace /> },
      { path: "eventsmoderation", element: <Navigate to={PORTAL_EVENTS_MODERATION} replace /> },
      {
        path: "eventsregistrationmoderation",
        element: <Navigate to={PORTAL_EVENTS_REGISTRATIONS} replace />,
      },
      { path: "projects/portal", element: <Navigate to={PORTAL_PROJECTS} replace /> },
      { path: "deposit", element: <Navigate to={PORTAL_DEPOSIT} replace /> },
      { path: "contacts", element: <ContactsPage /> },
      { path: "about", element: <AboutPage /> },
      { path: "help/agreement", element: <AgreementPage /> },
      { path: "help/personal", element: <PersonalDataConsentPage /> },
      { path: "help/privacy", element: <PolicyPrivacyPage /> },
      { path: "help/projects/legal/:slug", element: <ProjectConsentStubPage /> },
      {
        path: "profile",
        element: (
          <RequireAuth>
            <ProfilePage />
          </RequireAuth>
        ),
      },
      { path: "*", element: <NotFound /> },
    ],
  },
]

export default routes
